import type { CNSActionReceipt } from "@/models/cns";

export const ACTION_RECEIPT_DISPOSITIONS = [
  "APPLIED",
  "SATISFIED",
  "SUPERSEDED",
  "FAILED",
] as const;

export type ActionReceiptDisposition =
  (typeof ACTION_RECEIPT_DISPOSITIONS)[number];

const ACTION_RECEIPT_DISPOSITION_SET = new Set<string>(
  ACTION_RECEIPT_DISPOSITIONS,
);

/**
 * Reads the server-authored effect of a terminal Action Request.
 *
 * The compatibility branch exists only for receipts persisted before the disposition field was
 * introduced. A present but unknown value fails closed instead of entering an action-specific
 * success renderer.
 */
export function readActionReceiptDisposition(
  receipt: Readonly<Record<string, unknown>>,
): ActionReceiptDisposition | undefined {
  if (!("disposition" in receipt)) {
    return legacyActionReceiptDisposition(receipt);
  }

  const disposition = receipt.disposition;
  if (
    typeof disposition === "string" &&
    ACTION_RECEIPT_DISPOSITION_SET.has(disposition)
  ) {
    return disposition as ActionReceiptDisposition;
  }
  return undefined;
}

/** Compatibility for historical flattened receipts that predate server-authored disposition. */
function legacyActionReceiptDisposition(
  receipt: Readonly<Record<string, unknown>>,
): ActionReceiptDisposition {
  if (
    typeof receipt.status === "string" &&
    receipt.status.trim().toLowerCase() === "failed"
  ) {
    return "FAILED";
  }

  const completionReason = receipt.completionReason;
  if (
    typeof completionReason === "string" &&
    completionReason.trim().length > 0
  ) {
    return completionReason.trim() === "already_satisfied"
      ? "SATISFIED"
      : "SUPERSEDED";
  }

  return receipt.outcome === "superseded" || receipt.outcome === "not_selected"
    ? "SUPERSEDED"
    : "APPLIED";
}

/** Removes persistence and response-control fields before receipt data reaches Tool UI. */
export function projectActionReceiptData(
  data: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const projected = { ...data };
  delete projected.continuationMode;
  delete projected.receiptDisposition;
  delete projected.completionReason;
  delete projected.selectedActionRequestId;
  return projected;
}

/** Projects the canonical receipt envelope into the flat result shape consumed by Tool UI renderers. */
export function projectCanonicalActionReceipt(
  receipt: CNSActionReceipt,
): Record<string, unknown> {
  return {
    ...projectActionReceiptData(receipt.data),
    actionType: receipt.actionType,
    disposition: receipt.disposition,
    summary: receipt.summary,
    status: receipt.status,
    completedAt: receipt.completedAt,
  };
}
